import { EnterpriseInquiryForm } from "@/components/billing/EnterpriseInquiryForm";
import { ContactSuccessIcon } from "@/components/ContactSuccessIcon";
import { config } from "@/config";
import { ROUTES } from "@/constants/routes";
import { Button } from "@cohesive/ui-components";
import { motion } from "framer-motion";
import { Home } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export function EnterpriseInquiryPage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleSuccess = () => {
    setIsSubmitted(true);
  };

  const handleClose = () => {
    navigate(ROUTES.SETTINGS.USAGE);
  };

  return (
    <>
      <title>Enterprise Plan Inquiry</title>

      {config.recaptcha.siteKey && (
        <script
          async
          src={`https://www.google.com/recaptcha/enterprise.js?render=${config.recaptcha.siteKey}`}
        ></script>
      )}

      {isSubmitted ? (
        <div className="relative pt-8 pb-20 bg-background overflow-hidden">
          <div className="container mx-auto px-2">
            <div className="max-w-5xl mx-auto">
              <div className="px-4 sm:px-8 lg:px-12 relative">
                {/* Subtle background decoration */}
                <div className="absolute inset-0" />

                <div className="relative z-10 text-center space-y-8">
                  {/* Success icon with animation */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 260, damping: 20 }}
                    className=" flex items-center justify-center mx-auto"
                  >
                    <ContactSuccessIcon className="w-20 h-20 text-lc-active dark:text-lc-active" />
                  </motion.div>

                  {/* Main heading */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                    className="space-y-4"
                  >
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground leading-tight">
                      Inquiry Received
                    </h1>
                    <p className="text-xl font-light text-foreground/80 max-w-2xl mx-auto">
                      Thank you for your interest in our Enterprise plan. We're
                      excited to learn more about your organization's needs!
                    </p>
                    <div className="w-16 h-1 bg-foreground/20 rounded-full mx-auto"></div>
                  </motion.div>

                  {/* What happens next */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.4 }}
                    className="bg-muted/30 rounded-lg p-6 lg:p-8 space-y-6"
                  >
                    <h2 className="text-2xl md:text-3xl mb-16 font-semibold text-foreground flex items-center justify-center gap-2">
                      What happens next?
                    </h2>
                    <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                      <div className="space-y-3">
                        <div className="w-12 h-12 bg-foreground/10 rounded-full flex items-center justify-center mx-auto">
                          <span className="text-foreground font-bold text-lg">
                            1
                          </span>
                        </div>
                        <div className="space-y-2">
                          <h3 className="font-semibold text-foreground text-lg">
                            Review
                          </h3>
                          <p className="text-foreground/80 font-light">
                            Our Enterprise team will review your requirements
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="w-12 h-12 bg-foreground/10 rounded-full flex items-center justify-center mx-auto">
                          <span className="text-foreground font-bold text-lg">
                            2
                          </span>
                        </div>
                        <div className="space-y-2">
                          <h3 className="font-semibold text-foreground text-lg">
                            Contact
                          </h3>
                          <p className="text-foreground/80 font-light">
                            We'll reach out to schedule a personalized demo
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="w-12 h-12 bg-foreground/10 rounded-full flex items-center justify-center mx-auto">
                          <span className="text-foreground font-bold text-lg">
                            3
                          </span>
                        </div>
                        <div className="space-y-2">
                          <h3 className="font-semibold text-foreground text-lg">
                            Propose
                          </h3>
                          <p className="text-foreground/80 font-light">
                            Get a custom Enterprise plan tailored to your needs
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Back to home button */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 1 }}
                    className="pt-6 border-t"
                  >
                    <Button
                      onClick={() => navigate(ROUTES.HOME)}
                      variant="outline"
                      size="lg"
                    >
                      <Home className="w-5 h-5" />
                      Back to Home
                    </Button>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="container mx-auto max-w-7xl py-8 px-6">
          <EnterpriseInquiryForm
            onSuccess={handleSuccess}
            onClose={handleClose}
          />
        </div>
      )}
    </>
  );
}
